import { supporterWalletsQuery, supporterWalletTransactionsQuery } from "@/api/supporter";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { Card } from "@/components/ui/Card";
import { ButtonLink } from "@/components/ui/Link";
import { TabNav, TabNavItem } from "@/components/ui/TabNav";
import { useSupporterMoneyFormatters } from "@/hooks/usePreferredCurrencyState";
import { locale } from "@common/utils/i18n";
import { Box, Flex, QueryHandler, Span, Text } from "@givenwell/components";
import { AggregateSupporterTransaction } from "@givenwell/management-api";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/supporter/$supporterId/financial-reports/transactions/")({
  component: Page,
});

function Page() {
  const { supporterId } = Route.useParams();
  const nav = Route.useNavigate();
  const format = useSupporterMoneyFormatters();
  const search = Route.useSearch();
  const { page, "page-size": pageSize, curr: tokenCurrency } = search;
  const walletBalanceQuery = useQuery(supporterWalletsQuery(supporterId));
  const defaultCurrency = walletBalanceQuery.data?.wallets[0]?.currency;
  const transactionsQuery = useQuery(supporterWalletTransactionsQuery(supporterId, tokenCurrency, page, pageSize));

  const hasSingleWallet = (walletBalanceQuery.data?.wallets.length ?? 0) === 1;

  return (
    <QueryHandler
      query={transactionsQuery}
      success={({ data: transactions }) => (
        <Box css={{ spaceY: 16 }}>
          <Card title={`Current Balance${hasSingleWallet ? "" : "s"}`} css={{}}>
            <QueryHandler
              query={walletBalanceQuery}
              success={({ data: response }) => {
                if (hasSingleWallet) {
                  const wallet = response.wallets[0]!;
                  return <Balance formattedAmount={format(wallet.balance, wallet.currency)} />;
                }

                return (
                  <Flex css={{ gap: 16 }}>
                    <TabNav>
                      {response.wallets.map(wallet => (
                        <TabNavItem key={wallet.code} to="." search={{ ...search, curr: wallet.currency }}>
                          <Balance key={wallet.code} formattedAmount={format(wallet.balance, wallet.currency)} />
                        </TabNavItem>
                      ))}
                    </TabNav>
                  </Flex>
                );
              }}
            />
          </Card>
          <DataTable css={{}}>
            <DataTableBody
              data={transactions.transactions}
              rounded="top"
              columns={[
                {
                  header: "Date",
                  cell: t => (
                    <Text css={{ color: colors.text2 }}>
                      {new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(
                        new Date(t.timestamp),
                      )}
                    </Text>
                  ),
                  css: { px: 20 },
                  getCellCss: item => ({
                    pl: 20 - 4,
                    borderLeft: `4px solid ${item.transaction_amount > 0 ? colors.green300 : colors.blue50}`,
                  }),
                },
                {
                  header: "Transaction Description",
                  cell: t => (
                    <>
                      <Box>{getTransactionDescription(t)}</Box>
                      {t.detail_count == 1 ? null : t.transaction_type === "allowance" ? (
                        <Box css={{ mx: -8 }}>
                          <ButtonLink
                            size="sm"
                            variant="plain"
                            to="/supporter/$supporterId/financial-reports/transactions/allowance/$batchId"
                            search={{ page, "page-size": pageSize }}
                            params={{
                              supporterId,
                              batchId: t.transaction_ref,
                            }}
                          >
                            View details
                          </ButtonLink>
                        </Box>
                      ) : (
                        <>
                          <ButtonLink
                            size="sm"
                            variant="plain"
                            to="/supporter/$supporterId/financial-reports/transactions/details/$version"
                            search={{ page, "page-size": pageSize }}
                            params={{
                              supporterId,
                              version: t.version,
                            }}
                          >
                            View details
                          </ButtonLink>
                        </>
                      )}
                    </>
                  ),
                  css: { px: 24 },
                },
                {
                  header: "Amount Change",
                  cell: t => <>{format(t.transaction_amount, tokenCurrency ?? defaultCurrency ?? "nztoken").amount}</>,
                  css: { px: 24, align: "right" },
                },
                {
                  header: "Balance",
                  cell: t => <>{format(t.balance_after, tokenCurrency ?? defaultCurrency ?? "nztoken").amount}</>,
                  css: { px: 24, align: "right" },
                },
              ]}
              dataKey="version"
            />
            <DataTableFooter itemCount={transactions.total_count} />
          </DataTable>
        </Box>
      )}
    />
  );
}

type FormatResult = { amount: string; amountAsNumber: number; currency: string };
type BalanceProps = { formattedAmount: FormatResult };
function Balance({ formattedAmount }: BalanceProps) {
  return (
    <Text css={{ fontScale: "3xl", weight: 500, fontFamily: fonts.ftHabit }}>
      {formattedAmount.amount}
      <Span css={{ fontSize: `${2 / 3}em` }}> {formattedAmount.currency}</Span>
    </Text>
  );
}

function getTransactionDescription(transaction: AggregateSupporterTransaction) {
  // Payment received
  if (
    transaction.transaction_type === "pay_in" ||
    (transaction.transaction_type === "purchase" && transaction.transaction_amount > 0)
  ) {
    return <>Payment received</>;
  }

  if (transaction.transaction_type === "token_expiry") {
    return <>Member points expired</>;
  }

  return transaction.note || "";
}
